import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.closest('product-card')) return;

    const sourceProductId = event.target.dataset.productId;
    const isSizeSelection = event.detail?.isSizeSelection === true;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, newProduct, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const isSameProduct =
          !productId || !sourceProductId || String(productId) === String(sourceProductId);

        // A Size change normally keeps the same media set. Reuse the existing
        // gallery only when the server returned the same ordered media and label
        // markup; Color and combined-product changes keep the full replacement.
        if (isSizeSelection && isSameProduct && !newProduct && this.isConnected && this.slideshow) {
          const currentMedia = Array.from(this.querySelectorAll('.product-media[data-media-id]'));
          const nextMedia = Array.from(newMediaGallery.querySelectorAll('.product-media[data-media-id]'));
          const mediaSignature = (items) => items.map((item) => item.dataset.mediaId || '').join(',');
          const labelSignature = (root) =>
            Array.from(root.querySelectorAll('.bmp-product-labels'))
              .map((label) => label.outerHTML)
              .join('');

          if (
            mediaSignature(currentMedia) === mediaSignature(nextMedia) &&
            labelSignature(this) === labelSignature(newMediaGallery)
          ) {
            const featuredMediaId = resource?.featured_media?.id;
            const targetMedia = currentMedia.find(
              (mediaItem) => String(mediaItem.dataset.mediaId) === String(featuredMediaId)
            );
            const targetSlide = targetMedia?.closest('slideshow-slide');
            const slides = Array.from(this.slideshow.slides ?? []);
            const targetIndex = targetSlide ? slides.indexOf(targetSlide) : -1;

            if (targetIndex >= 0) {
              this.slideshow.select(targetIndex, undefined, { animate: false });
              return;
            }
          }
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
